/**
 * useUpdateProfile - Hook for updating the authenticated user's profile.
 *
 * On success, syncs the updated user data to both localStorage and React Query cache
 * so the UI reflects changes immediately (e.g., UserMenu display name).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateProfile } from '@/lib/api';
import type { UpdateProfileRequest, UserInfoResponse } from '@/types/api';
import { storeUser, authQueryKeys } from '../utils/authStorage';
import type { AuthUser } from '../types';

/**
 * Hook for updating user profile.
 *
 * @returns Mutation object with:
 * - `mutateAsync`: Async function to trigger profile update
 * - `isPending`: Boolean indicating if the request is in progress
 * - `error`: Error object if the request failed
 * - `isSuccess`: Boolean indicating if the profile was updated
 * - `reset`: Function to reset the mutation state
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateProfileRequest) => updateProfile(request),
    onSuccess: (updatedUser: UserInfoResponse) => {
      const authUser: AuthUser = updatedUser;
      storeUser(authUser);
      queryClient.setQueryData(authQueryKeys.currentUser(), authUser);
    },
  });
}
